import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GraduationCap, Loader2, Shield, Eye, EyeOff, Sparkles } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.mustChangePassword) {
        // The ChangePasswordModal will be shown automatically
        toast.info('Please set a new password to continue');
      } else {
        toast.success('Welcome back!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <GraduationCap size={28} />
            </div>
            <h1 className="login-title">Frame Interventions</h1>
            <p className="login-subtitle">MTSS Progress Tracker</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="form-group">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B] focus:ring-[#2C4C3B]/30"
              />
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-[#2C4C3B] hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                  className="rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B] focus:ring-[#2C4C3B]/30 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              disabled={loading}
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="login-footer">
            <div className="flex flex-col items-center gap-3">
              {/* Explore Button - Enhanced visibility */}
              <Link to="/explore" className="w-full group">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full bg-[#F0F7F4] border-[#2C4C3B]/30 text-[#2C4C3B] hover:bg-[#E5F0EA] hover:border-[#2C4C3B]/50 hover:shadow-md rounded-xl transition-all duration-300 group-hover:-translate-y-0.5"
                  data-testid="explore-btn"
                  style={{
                    animation: 'subtle-pulse 3s ease-in-out infinite'
                  }}
                >
                  <Sparkles className="mr-2 h-4 w-4 text-[#2C4C3B]/70 group-hover:text-[#2C4C3B]" />
                  Explore Frame Interventions
                </Button>
              </Link>
              <p className="text-xs text-[#595959] text-center">
                Take a quick visual tour before logging in
              </p>
              
              {/* Subtle pulse animation - respects reduced motion */}
              <style>{`
                @keyframes subtle-pulse {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(44, 76, 59, 0); }
                  50% { box-shadow: 0 0 0 4px rgba(44, 76, 59, 0.1); }
                }
                @media (prefers-reduced-motion: reduce) {
                  .group button { animation: none !important; }
                }
              `}</style>
              
              <div className="border-t border-[#E5E5E5] w-full pt-3 mt-1">
                <div className="flex items-center justify-center gap-2 text-[#595959] text-sm">
                  <Shield className="h-4 w-4" />
                  <span>Contact your administrator for account access</span>
                </div>
                <p className="text-xs text-[#A3A3A3] text-center mt-2">Part of the <span className="font-semibold text-[#2C4C3B]">FrameEd</span> ecosystem</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
